import { useAuth } from "@/context/AuthContext";
import { SaveDialogContent, SaveButton, SaveForm } from "@/components/SaveForm";
import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Layers3, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = { nome: "", descricao: "", valor_mensal: 0, max_usuarios: 0, max_pecas: 0, module_ids: [], ativo: true };
const inp = "bg-slate-950 border-slate-700 text-slate-100 mt-1";
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });


function ModulePicker({ state, setter, modules }) {
  const selected = state.module_ids || [];
  const allIds = modules.map((m) => m.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));

  const toggleModule = (id) => {
    setter((current) => {
      const cur = current.module_ids || [];
      return {
        ...current,
        module_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
      };
    });
  };

  const selectAll = () => setter((current) => ({ ...current, module_ids: allIds }));
  const clearAll = () => setter((current) => ({ ...current, module_ids: [] }));

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 px-3 py-2">
        <div className="text-xs text-slate-400">
          {selected.length} de {modules.length} selecionado(s)
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={allSelected}
            onClick={selectAll}
            className="h-8 text-xs text-emerald-300 hover:text-emerald-200"
          >
            Marcar todos
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={!selected.length}
            onClick={clearAll}
            className="h-8 text-xs text-slate-300"
          >
            Desmarcar todos
          </Button>
        </div>
      </div>

      <div className="max-h-56 overflow-y-auto p-3 space-y-2">
        {modules.map((m) => (
          <label key={m.id} className="flex items-center justify-between gap-3 rounded-lg px-2 py-2 hover:bg-slate-900/60">
            <div>
              <div className="text-sm text-slate-200">{m.nome}</div>
              <div className="text-[11px] text-slate-500">{m.descricao}</div>
            </div>
            <Switch
              checked={selected.includes(m.id)}
              onCheckedChange={() => toggleModule(m.id)}
            />
          </label>
        ))}
      </div>
    </div>
  );
}

export default function PlatformPlans() {
  const { user } = useAuth();
  const owner = user?.is_platform_owner === true;
  const payload = value => owner ? value : Object.fromEntries(["nome", "descricao", "module_ids", "max_usuarios", "max_pecas"].map(k => [k, value[k]]));
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, m] = await Promise.all([api.get("/platform/plans"), api.get("/platform/modules")]);
      setPlans(p.data); setModules(m.data.filter((x) => x.ativo !== false));
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.nome) return toast.error("Informe o nome do plano.");
    setBusy(true);
    try {
      await api.post("/platform/plans", payload(form));
      toast.success(owner ? "Plano criado." : "Plano criado inativo. O proprietário define o preço e ativa.");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/platform/plans/${edit.id}`, payload(edit));
      toast.success("Plano atualizado.");
      setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-50">Planos</h1>
          <p className="text-sm text-slate-400">Defina preço, limites e módulos incluídos em cada plano.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-violet-700 hover:bg-violet-600"><Plus className="h-4 w-4 mr-1" /> Novo plano</Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {plans.map((p) => (
          <Card key={p.id} className="card-metal p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2"><Layers3 className="h-5 w-5 text-violet-300" /><span className="font-semibold text-slate-100">{p.nome}</span></div>
                <div className="mt-3 text-2xl font-bold text-slate-50">{brl(p.valor_mensal)}<span className="text-xs font-normal text-slate-500">/mês</span></div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setEdit({ ...p, module_ids: [...(p.module_ids || [])] })}><Pencil className="h-4 w-4" /></Button>
            </div>
            <p className="mt-3 text-sm text-slate-400 min-h-10">{p.descricao || "Sem descrição."}</p>
            <div className="mt-4 text-xs text-slate-400 space-y-1">
              <div>{p.module_ids?.length || 0} módulo(s)</div>
              <div>Usuários: {p.max_usuarios || "Ilimitado"}</div>
              <div>Peças: {p.max_pecas || "Ilimitado"}</div>
              <div className={p.ativo ? "text-emerald-400" : "text-rose-400"}>{p.ativo ? "Ativo" : "Inativo"}</div>
            </div>
          </Card>
        ))}
        {!plans.length && <Card className="card-metal p-8 text-center text-slate-500 lg:col-span-3">Nenhum plano cadastrado.</Card>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
          <SaveForm disabled={busy} onSave={create} className="space-y-4">
            <DialogHeader><DialogTitle>Novo plano</DialogTitle></DialogHeader>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label>Nome *</Label><Input className={inp} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Descrição</Label><Input className={inp} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></div>
{owner && <div><Label>Valor mensal</Label><Input type="number" step="0.01" className={inp} value={form.valor_mensal} onChange={(e) => setForm({ ...form, valor_mensal: e.target.value })} /></div>}
              <div><Label>Máx. usuários (0 = ilimitado)</Label><Input type="number" className={inp} value={form.max_usuarios} onChange={(e) => setForm({ ...form, max_usuarios: e.target.value })} /></div>
              <div><Label>Máx. peças (0 = ilimitado)</Label><Input type="number" className={inp} value={form.max_pecas} onChange={(e) => setForm({ ...form, max_pecas: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Módulos incluídos</Label><ModulePicker state={form} setter={setForm} modules={modules} /></div>
            </div>
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><SaveButton type="submit" disabled={busy} className="bg-violet-700 hover:bg-violet-600">Criar plano</SaveButton></DialogFooter>
          </SaveForm>
        </SaveDialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
          <SaveForm disabled={busy} onSave={saveEdit} className="space-y-4">
            <DialogHeader><DialogTitle>Editar plano</DialogTitle></DialogHeader>
            {edit && <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label>Nome</Label><Input className={inp} value={edit.nome || ""} onChange={(e) => setEdit({ ...edit, nome: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Descrição</Label><Input className={inp} value={edit.descricao || ""} onChange={(e) => setEdit({ ...edit, descricao: e.target.value })} /></div>
<div><Label>Valor mensal{!owner && " (somente leitura)"}</Label><Input readOnly={!owner} type="number" step="0.01" className={inp} value={edit.valor_mensal ?? 0} onChange={(e) => setEdit({ ...edit, valor_mensal: e.target.value })} /></div>
              <div><Label>Máx. usuários</Label><Input type="number" className={inp} value={edit.max_usuarios ?? 0} onChange={(e) => setEdit({ ...edit, max_usuarios: e.target.value })} /></div>
              <div><Label>Máx. peças</Label><Input type="number" className={inp} value={edit.max_pecas ?? 0} onChange={(e) => setEdit({ ...edit, max_pecas: e.target.value })} /></div>
              <div className="flex items-center justify-between rounded-lg bg-slate-950/60 px-3 py-3"><Label>Plano ativo</Label><Switch disabled={!owner} checked={edit.ativo !== false} onCheckedChange={(v) => setEdit({ ...edit, ativo: v })} /></div>
              <div className="sm:col-span-2"><Label>Módulos incluídos</Label><ModulePicker state={edit} setter={setEdit} modules={modules} /></div>
            </div>}
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><SaveButton type="submit" disabled={busy} className="bg-violet-700 hover:bg-violet-600">Salvar alterações</SaveButton></DialogFooter>
          </SaveForm>
        </SaveDialogContent>
      </Dialog>
    </div>
  );
}
